import { useEffect, useState } from 'react';
import CommonApi from '../../Api/CommonApi';
import { OldManualList } from '../../Models/OldManualList';
import ManufacturerApi from '../../Api/ManufacturerApi';
import { Manufacturer } from '../../Models/Manufacturer';
import EquipmentApi from '../../Api/EquipmentApi';
import { toast } from 'react-toastify';

interface Props {
  isOpen: boolean;
  manualId: string;
  onClose: () => void;
  onSaved?: () => void;
}

export default function OldManualEditModal({
  isOpen,
  manualId,
  onClose,
  onSaved,
}: Props) {
  const [manual, setManual] = useState<OldManualList | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manufacturerList, setManufacturerList] = useState<Manufacturer[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    getManufacturerList();
    if (manualId === '0') {
      // Create a new blank manual object
      setManual({
        id: 0,
        manufacturerId: '0',
        subCategoryName: '',
        fileName: '',
        fullUrl: '',
      } as OldManualList);
      setLoading(false);
    } else {
      CommonApi.getOldManual(manualId)
        .then((res) => {
          setManual(res.data);
          if (res.data.manufacturerId) {
            getOldManuals(Number(res.data.manufacturerId));
          }
        })
        .catch(() => setError('Failed to load manual'))
        .finally(() => setLoading(false));
    }
  }, [manualId, isOpen]);

  const getManufacturerList = () => {
    ManufacturerApi.getManufacturerListWG()
      .then((res) => setManufacturerList(res.data))
      .catch((err) => console.error('Failed to load manufacturer list', err));
  };

  const getOldManuals = (manufacturerId: number) => {
    CommonApi.getOldManuals(manufacturerId.toString())
      .then((res) => {
        const subs = res.data
          .map((m) => m.subCategoryName?.trim())
          .filter((v): v is string => !!v);

        setSubCategoryOptions([...new Set(subs)].sort());
      })
      .catch((err) => {
        console.log('Error loading old manuals', err);
      });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setManual((prev) => {
      const updated = prev ? { ...prev, [name]: value } : null;
      if (name === 'manufacturerId' && Number(value)) {
        getOldManuals(Number(value));
      }
      return updated;
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('file', file);
    fd.append('directory', 'manuals');

    await EquipmentApi.addFile(fd)
      .then((res) => {
        if (res.data.success) {
          setManual((prev) =>
            prev
              ? {
                  ...prev,
                  fullUrl: res.data.affectedEntityIdentifier,
                  fileName: file.name,
                }
              : prev
          );
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((err) => toast.error('Error: ' + err));
  };

  const handleSave = () => {
    if (!manual) return;
    setSaving(true);
    CommonApi.updateOldManual(manual)
      .then(() => {
        toast.success('Manual updated');
        onSaved?.();
        onClose();
      })
      .catch(() => setError('Failed to save manual'))
      .finally(() => setSaving(false));
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center'>
      <div className='bg-white p-6 rounded-lg shadow-lg max-w-lg w-full relative'>
        <button
          className='absolute top-2 right-3 text-gray-600 text-xl'
          onClick={onClose}
        >
          &times;
        </button>

        <h2 className='text-2xl font-bold mb-4'>Edit Manual</h2>

        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p className='text-red-600'>{error}</p>
        ) : !manual ? (
          <p>Manual not found.</p>
        ) : (
          <>
            <div className='mb-3'>
              <label className='block font-medium'>Manufacturer</label>
              <select
                name='manufacturerId'
                value={manual.manufacturerId ?? ''}
                onChange={handleChange}
                className='w-full border p-2'
              >
                <option value=''>-- Select Manufacturer --</option>
                {manufacturerList.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div className='mb-3'>
              <label className='block font-medium'>Subcategory</label>
              <input
                list='subCategoryOptions'
                name='subCategoryName'
                value={manual.subCategoryName ?? ''}
                onChange={handleChange}
                className='w-full border p-2'
              />
              <datalist id='subCategoryOptions'>
                {subCategoryOptions.map((opt) => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            </div>
            <div className='mb-3'>
              <label className='block font-medium'>Upload PDF</label>
              <input
                type='file'
                accept='application/pdf'
                onChange={handleFileUpload}
                className='w-full border p-2'
              />
              {manual.fullUrl && (
                <a
                  href={manual.fullUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-600 underline text-sm mt-1 block'
                >
                  View current file
                </a>
              )}
            </div>

            <div className='mb-3'>
              <label className='block font-medium'>File Name</label>
              <input
                type='text'
                name='fileName'
                value={manual.fileName ?? ''}
                onChange={handleChange}
                className='w-full border p-2'
              />
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className='bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700'
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
